import { ColumnDef, RowData } from '@tanstack/react-table';
import { ColumnSortableButton, ColumnSortableIcon } from '@/components/table/column-sortable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { format } from 'date-fns';
import { ArrowUpDown, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Time } from '@/components/ui/time';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: 'left' | 'right' | 'center';
    style?: React.CSSProperties;
    label?: string;
  }
}

/** @deprecated Use `<Time>` component from `@/components/ui/time` instead */
export const formatDate = (date?: Date | string) => {
  if (!date) return '';
  return format(new Date(date), 'PPp');
};

/** @deprecated Use `<Time>` component from `@/components/ui/time` instead */
export const formatDateShort = (date?: Date | string) => {
  if (!date) return '';
  return format(new Date(date), 'PP');
};

export interface ColumnHelperOptions<TData> {
  id: string;
  label: string;
  accessorKey?: keyof TData;
  enableHiding?: boolean;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  defaultSort?: 'asc' | 'desc';
}

export function createTextColumn<TData>(
  options: ColumnHelperOptions<TData> & {
    cell?: (value: any, row: TData) => React.ReactNode;
  }
): ColumnDef<TData> {
  return {
    id: options.id,
    accessorKey: options.accessorKey,
    enableHiding: options.enableHiding ?? true,
    meta: {
      label: options.label,
      align: options.align ?? 'left',
    },
    header: options.sortable ? (props) => (
      <ColumnSortableButton {...props} defaultSort={options.defaultSort}>
        {options.label}
        <ColumnSortableIcon {...props} />
      </ColumnSortableButton>
    ) : options.label,
    cell: options.cell ? ({ row }) => options.cell!(row.getValue(options.id), row.original) as React.ReactNode : undefined,
  };
}

export function createDateColumn<TData>(
  options: ColumnHelperOptions<TData> & {
    short?: boolean;
  }
): ColumnDef<TData> {
  return {
    id: options.id,
    accessorKey: options.accessorKey,
    enableHiding: options.enableHiding ?? true,
    meta: {
      label: options.label,
      align: options.align ?? 'left',
    },
    header: options.sortable ? (props) => (
      <ColumnSortableButton {...props} defaultSort={options.defaultSort ?? 'desc'}>
        {options.label}
        <ColumnSortableIcon {...props} />
      </ColumnSortableButton>
    ) : options.label,
    cell: ({ row }) => {
      const date = row.getValue(options.id) as Date | string | undefined;
      return (
        <span className="opacity-60">
          <Time date={date} format={options.short ? 'date' : 'datetime'} />
        </span>
      ) as React.ReactNode;
    },
  };
}

export function createLinkColumn<TData>(
  options: ColumnHelperOptions<TData> & {
    getHref: (row: TData) => string;
    external?: boolean;
  }
): ColumnDef<TData> {
  return {
    id: options.id,
    accessorKey: options.accessorKey,
    enableHiding: options.enableHiding ?? true,
    meta: {
      label: options.label,
      align: options.align ?? 'left',
    },
    header: options.sortable ? (props) => (
      <ColumnSortableButton {...props} defaultSort={options.defaultSort}>
        {options.label}
        <ColumnSortableIcon {...props} />
      </ColumnSortableButton>
    ) : options.label,
    cell: ({ row }) => {
      const value = row.getValue(options.id);
      const href = options.getHref(row.original);
      
      if (options.external) {
        return (
          <a 
            href={href} 
            target="_blank" 
            rel="noopener noreferrer"
            className="font-semibold text-lime hover:underline inline-flex items-center gap-1"
          >
            {value as React.ReactNode}
            <ExternalLink className="h-3 w-3" />
          </a>
        );
      }
      
      return (
        <Link to={href} className="font-semibold text-lime hover:underline">
          {value as React.ReactNode}
        </Link>
      );
    },
  };
}

export function createBadgeColumn<TData>(
  options: ColumnHelperOptions<TData> & {
    getBadgeProps: (value: any, row: TData) => {
      variant?: 'default' | 'secondary' | 'destructive' | 'outline';
      className?: string;
      children: React.ReactNode;
    };
  }
): ColumnDef<TData> {
  return {
    id: options.id,
    accessorKey: options.accessorKey,
    enableHiding: options.enableHiding ?? true,
    meta: {
      label: options.label,
      align: options.align ?? 'left',
    },
    header: options.sortable ? (props) => (
      <ColumnSortableButton {...props} defaultSort={options.defaultSort}>
        {options.label}
        <ColumnSortableIcon {...props} />
      </ColumnSortableButton>
    ) : options.label,
    cell: ({ row }) => {
      const value = row.getValue(options.id);
      const badgeProps = options.getBadgeProps(value, row.original);
      return <Badge {...badgeProps}>{badgeProps.children}</Badge>;
    },
  };
}

export function createActionsColumn<TData>(
  options: {
    id?: string;
    getActions: (row: TData) => Array<{
      label: string;
      onClick: () => void;
      variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
      disabled?: boolean;
    }>;
  }
): ColumnDef<TData> {
  return {
    id: options.id ?? 'actions',
    enableHiding: false,
    meta: {
      label: 'Actions',
      align: 'right',
    },
    header: 'Actions',
    cell: ({ row }) => {
      const actions = options.getActions(row.original);
      return (
        <div className="flex gap-2 justify-end">
          {actions.map((action, index) => (
            <Button
              key={index}
              variant={action.variant ?? 'outline'}
              size="sm"
              onClick={action.onClick}
              disabled={action.disabled}
            >
              {action.label}
            </Button>
          ))}
        </div>
      ) as React.ReactNode;
    },
  };
}